import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Package } from "lucide-react";
import { getFeaturedRanges, getRangePath } from "../../config/ranges";
import { getCategoryName } from "../../config/categories";
import { getStatusMeta } from "../../config/statuses";
import ProductGalleryPlaceholder from "../product/ProductGalleryPlaceholder";
import { getPrimaryProductImage } from "../../lib/productMedia";

/**
 * Featured portfolio — driven by featured Product Ranges.
 */
export default function FeaturedProducts() {
  const ranges = getFeaturedRanges().slice(0, 3);

  return (
    <section id="products" className="relative py-28 lg:py-40" data-testid="featured-products">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-6">
            <div className="sec-index mb-4">03 — Portfolio</div>
            <h2 className="display display-section text-4xl md:text-6xl">
              Built today.
              <br />
              <span style={{ color: "var(--gold)" }}>Building tomorrow.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-base md:text-lg text-steel-text leading-relaxed">
              We show our work honestly — product families shipping today, platforms
              in development, and research still forming. No product is ever presented
              as more finished than it is.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {ranges.map((r, i) => {
            const status = getStatusMeta(r.status);
            const to = getRangePath(r);
            const cta =
              r.status === "Under Development" || r.status === "Research"
                ? r.status === "Research"
                  ? "Research Preview"
                  : "Learn More"
                : "View Range";

            return (
              <Link
                key={r.id}
                to={to}
                data-testid={`product-link-${r.slug}`}
                className="block h-full"
              >
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.1, ease: [0.19, 1, 0.22, 1] }}
                  className="steel-card clip-frame group h-full p-0 flex flex-col min-h-[380px] overflow-hidden"
                  data-testid={`product-${r.slug}`}
                >
                  <div className="border-b border-border overflow-hidden">
                    {getPrimaryProductImage(r) ? (
                      <img
                        src={getPrimaryProductImage(r)}
                        alt={r.name}
                        className="w-full aspect-[16/10] object-cover object-center group-hover:scale-[1.02] transition-transform duration-700"
                        loading="lazy"
                      />
                    ) : (
                      <div className="relative">
                        <ProductGalleryPlaceholder compact label="Range Imagery" />
                        <div className="absolute top-4 left-4 w-10 h-10 border border-border bg-background/80 flex items-center justify-center">
                          <Package size={18} className="text-[color:var(--steel)]" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-9 flex flex-col flex-1">
                    <div className="flex items-center justify-between">
                      <div className="font-mono-2 text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                        {r.subcategory || getCategoryName(r.category)}
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ background: status.color }}
                        />
                        <span
                          className="font-mono-2 text-[10px] tracking-[0.18em] uppercase"
                          style={{ color: status.color }}
                        >
                          {status.label}
                        </span>
                      </div>
                    </div>

                    <div className="mt-auto pt-8">
                      <h3 className="display display-section text-2xl md:text-3xl">{r.name}</h3>
                      {r.rangeLabel && (
                        <div className="mt-2 font-mono-2 text-[11px] tracking-[0.18em] uppercase text-[color:var(--gold)]">
                          {r.rangeLabel}
                        </div>
                      )}
                      <p className="mt-4 text-sm text-steel-text leading-relaxed line-clamp-3">
                        {r.shortDescription || r.description}
                      </p>
                      <div
                        className="mt-7 flex items-center gap-2 font-mono-2 text-[11px] tracking-[0.2em] uppercase"
                        style={{ color: "var(--gold)" }}
                      >
                        {cta} <ArrowUpRight size={14} />
                      </div>
                    </div>
                  </div>
                </motion.div>
              </Link>
            );
          })}
        </div>

        <div className="mt-10 flex justify-end">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 font-mono-2 text-[11px] tracking-[0.2em] uppercase"
            style={{ color: "var(--gold)" }}
            data-testid="view-full-catalogue"
          >
            View full catalogue <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}
